<template>
	<div class="crRoot">
		<h1 class="pageHeading">Code Review</h1>
		<nav class="tabs" aria-label="Code review pages">
			<router-link :to="{ name: 'codeReviewQueue' }">Findings</router-link>
			<router-link :to="{ name: 'codeReviewPushes' }">Reviewed pushes</router-link>
			<router-link :to="{ name: 'codeReviewFixes' }" class="active" aria-current="page">Fixes</router-link>
			<router-link :to="{ name: 'codeReviewReport' }">Report</router-link>
		</nav>

		<div v-if="error" class="errorBox">{{error}}</div>
		<div v-if="loading && !data" class="loadingInline"><ScaleLoader /> Loading…</div>

		<template v-if="data">
			<!-- Settings in force -->
			<section class="card" aria-labelledby="crxSettingsHeading">
				<h2 id="crxSettingsHeading" class="secondaryHeading">Autonomous fixes</h2>
				<div v-if="!data.fixesEnabled" class="warningBox">Autonomous fixes are turned off (setting codeReviewFixesEnabled). Findings cannot be approved for fixes and no batch starts; existing batches can still be viewed and discarded.</div>
				<div v-else-if="!data.reviewsEnabled" class="warningBox">Code review is turned off (setting codeReviewEnabled), so queued fix batches wait until it is turned on.</div>
				<div v-if="data.fixesEnabled && !data.requirePushApproval" class="warningBox">Push approval is off (codeReviewFixRequirePushApproval): a clean batch is pushed as soon as its job ends.</div>
				<table class="infoTable">
					<tbody>
						<tr><th scope="row">Schedule</th><td>{{data.schedule ? data.schedule + ' (' + data.timeZone + ')' : 'manual only'}}<span v-if="data.nextScheduledAt" class="hintText"> · next {{formatTime(data.nextScheduledAt)}}</span><span v-if="data.scheduleError" class="errorText"> · {{data.scheduleError}}</span></td></tr>
						<tr><th scope="row">Push</th><td>{{data.requirePushApproval ? 'after a user approves it' : 'automatic for clean batches'}}</td></tr>
						<tr><th scope="row">Model</th><td class="mono">{{data.model}}</td></tr>
						<tr><th scope="row">Limits</th><td>{{formatCost(data.maxBudgetUsd)}} and {{data.timeoutMinutes}} minutes per job; at most {{data.maxFindingsPerBatch}} findings per batch</td></tr>
						<tr><th scope="row">Commit author</th><td class="mono">{{data.commitAuthor}}</td></tr>
						<tr><th scope="row">Allowed tools</th><td class="mono toolsCell">{{data.allowedTools}}</td></tr>
					</tbody>
				</table>
				<div class="hintText">Poller: {{data.pollerStatus}}</div>
			</section>

			<!-- Approved findings -->
			<section class="card" aria-labelledby="crxApprovedHeading">
				<div class="row sectionHead">
					<h2 id="crxApprovedHeading" class="secondaryHeading">Approved findings</h2>
					<button type="button" v-if="data.canApprove" @click="runBatch('')" :disabled="busy || !data.fixesEnabled || !approvedBatchTotal">Run fix batch now (all repositories)</button>
					<span v-if="busy" class="loadingInline"><ScaleLoader /> Working…</span>
				</div>
				<table v-if="data.approved.length" class="dense narrow">
					<thead>
						<tr>
							<th scope="col">Repository</th>
							<th scope="col">Approved (batch)</th>
							<th scope="col">Approved (immediate)</th>
							<th scope="col">In a batch</th>
							<th scope="col"><span class="visuallyHidden">Actions</span></th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="a in data.approved" :key="a.repo">
							<td>{{a.repo}}</td>
							<td><router-link :to="{ name: 'codeReviewQueue' }" @click="filterQueue(a.repo, 'ApprovedBatch')">{{a.batch}}</router-link></td>
							<td>{{a.immediate}}</td>
							<td>{{a.inProgress}}</td>
							<td><button type="button" v-if="data.canApprove" @click="runBatch(a.repo)" :disabled="busy || !data.fixesEnabled || !a.batch">Run fix batch now</button></td>
						</tr>
					</tbody>
				</table>
				<div v-else class="hintText">No findings are approved for a fix. Approve findings on their pages ("Approve for batch fix").</div>
				<div v-if="actionError" class="errorBox" role="alert">{{actionError}}</div>
				<div v-if="actionInfo" class="hintText" role="status">{{actionInfo}}</div>
				<div v-if="!data.canApprove" class="hintText">Running batches needs the Code Review Approve Fixes permission.</div>
			</section>

			<!-- Batches -->
			<section class="card tableCard" aria-labelledby="crxBatchesHeading">
				<div class="row sectionHead">
					<h2 id="crxBatchesHeading" class="secondaryHeading">Fix batches</h2>
					<button type="button" @click="load" :disabled="loading">Refresh</button>
					<span v-if="loading" class="loadingInline"><ScaleLoader /></span>
				</div>
				<table v-if="data.batches.length" class="dense">
					<thead>
						<tr>
							<th scope="col">Batch</th>
							<th scope="col">Repo</th>
							<th scope="col">Status</th>
							<th scope="col">Trigger</th>
							<th scope="col">Findings</th>
							<th scope="col">Commits</th>
							<th scope="col">Created</th>
							<th scope="col">Pushed</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="b in data.batches" :key="b.id" class="clickable" :class="{ attention: b.status === 'AwaitingPushApproval' || b.status === 'Failed' || b.status === 'Safety' }" @click="open(b, $event)">
							<td class="nowrap"><router-link :to="batchRoute(b)">{{b.displayId}}</router-link></td>
							<td class="nowrap">{{b.repo}}</td>
							<td class="statusCell">
								<span class="badge" :class="statusClass(b.status)">{{label(b.status)}}</span>
								<span v-if="b.hasUncommittedChanges" class="badge stProgress" title="The agent left uncommitted changes">uncommitted</span>
								<span v-if="b.warningCount" class="badge stProgress">{{b.warningCount}} warning{{b.warningCount === 1 ? '' : 's'}}</span>
								<div v-if="b.statusReason" class="reasonText">{{b.statusReason}}</div>
							</td>
							<td class="nowrap">{{b.trigger}}<div class="hintText">{{b.requestedBy}}</div></td>
							<td>
								<template v-for="(id, i) in b.findingIds" :key="id">{{i ? ', ' : ''}}<router-link :to="{ name: 'codeReviewFinding', params: { id: id } }">CR-{{id}}</router-link></template>
								<div v-if="b.commitCount" class="hintText">{{b.fixedCount}} fixed</div>
							</td>
							<td>{{b.commitCount}}</td>
							<td class="nowrap">{{formatTime(b.createdAt)}}</td>
							<td class="nowrap"><a v-if="b.pushedShaUrl" :href="b.pushedShaUrl" target="_blank" rel="noopener" class="mono">{{shortSha(b.pushedSha)}}</a></td>
						</tr>
					</tbody>
				</table>
				<div v-else class="hintText">No fix batches yet.</div>
			</section>
		</template>
	</div>
</template>

<script>
	import { GetFixes, RunFixBatch } from 'appRoot/api/CodeReviewData';
	import { label, statusClass, formatTime, formatCost, shortSha, loadStored, saveStored } from './codeReviewFormat';

	/** How often the page refreshes while a batch is queued, running or pushing. */
	const pollMs = 10000;

	export default {
		data()
		{
			return {
				data: null,
				loading: false,
				error: null,
				loadToken: 0,
				busy: false,
				actionError: null,
				actionInfo: null,
				timer: null
			};
		},
		created()
		{
			this.load();
		},
		beforeUnmount()
		{
			clearTimeout(this.timer);
		},
		computed:
		{
			approvedBatchTotal()
			{
				return this.data ? this.data.approved.reduce((sum, a) => sum + a.batch, 0) : 0;
			}
		},
		methods:
		{
			label,
			statusClass,
			formatTime,
			formatCost,
			shortSha,
			batchRoute(b)
			{
				return { name: 'codeReviewFixBatch', params: { id: b.id } };
			},
			open(b, e)
			{
				if (e && e.target && e.target.closest && e.target.closest('a, button'))
					return;
				this.$router.push(this.batchRoute(b));
			},
			filterQueue(repo, status)
			{
				// The queue page restores its filters from local storage.
				// Keep the saved sort order; replace the filters.
				let stored = loadStored('codeReview_queueFilters');
				let sort = stored && Array.isArray(stored.sort) ? stored.sort : undefined;
				saveStored('codeReview_queueFilters', { repos: [repo], minSeverity: '', text: '', statuses: [status], verdicts: [], sort: sort });
			},
			async load()
			{
				clearTimeout(this.timer);
				let token = ++this.loadToken;
				this.loading = true;
				this.error = null;
				try
				{
					let data = await GetFixes();
					if (token !== this.loadToken)
						return;
					if (data.success)
						this.data = data;
					else
						this.error = data.error;
				}
				catch (ex)
				{
					if (token === this.loadToken)
						this.error = ex.message;
				}
				finally
				{
					if (token === this.loadToken)
					{
						this.loading = false;
						if (this.data && this.data.batches.some(b => b.status === 'Queued' || b.status === 'Running' || b.status === 'Pushing'))
							this.timer = setTimeout(() => this.load(), pollMs);
					}
				}
			},
			async runBatch(repo)
			{
				if (this.busy)
					return;
				this.busy = true;
				this.actionError = null;
				this.actionInfo = null;
				try
				{
					let response = await RunFixBatch(repo || null);
					if (!response.success)
						this.actionError = response.error;
					else
						this.actionInfo = response.message;
					await this.load();
				}
				catch (ex)
				{
					this.actionError = ex.message;
				}
				finally
				{
					this.busy = false;
				}
			}
		}
	};
</script>

<style scoped src="./codeReview.css"></style>
<style scoped>
	.sectionHead
	{
		margin-bottom: 4px;
	}

		.sectionHead .secondaryHeading
		{
			margin: 0;
		}

	.tableCard
	{
		overflow-x: auto;
	}

	.dense.narrow
	{
		width: auto;
		min-width: 50%;
	}

	.statusCell
	{
		min-width: 200px;
	}

	.reasonText
	{
		font-size: 12px;
		color: #505050;
		margin-top: 2px;
		word-break: break-word;
	}

	.toolsCell
	{
		font-size: 12px;
		overflow-wrap: anywhere;
	}

	.errorText
	{
		color: #A00000;
	}

	tr.attention td:first-child
	{
		border-left: 3px solid #1A5FB4;
	}

	.visuallyHidden
	{
		position: absolute;
		width: 1px;
		height: 1px;
		margin: -1px;
		padding: 0;
		overflow: hidden;
		clip: rect(0, 0, 0, 0);
		border: 0;
	}
</style>
